import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import {
  Download,
  InsertDriveFile,
  Image,
  PictureAsPdf,
  VideoFile,
  AudioFile,
  Archive
} from '@mui/icons-material';
import apiClient from '../config/axios';
import dayjs from 'dayjs';
import 'dayjs/locale/it';

const CourseFilesMember = ({ courseId }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadFiles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.get(`/api/files/courses/${courseId}`);
      if (response.data.success) {
        setFiles(Array.isArray(response.data.data) ? response.data.data : []);
      } else {
        setError(response.data.message || 'Failed to load files');
      }
    } catch (err) {
      console.error('Error loading files:', err);
      setError(err.response?.data?.message || 'Failed to load files');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  // Load files on component mount
  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const handleDownload = async (file) => {
    try {
      setError(null);
      
      const encodedFileKey = encodeURIComponent(file.key);
      const response = await apiClient.get(`/api/files/courses/${courseId}/download/${encodedFileKey}`, {
        responseType: 'blob',
      });
      
      // Create blob and download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download error:', err);
      setError(err.response?.data?.message || 'Download failed');
    }
  };

  const getFileIcon = (contentType) => {
    if (!contentType) return <InsertDriveFile />;
    if (contentType.startsWith('image/')) return <Image />;
    if (contentType === 'application/pdf') return <PictureAsPdf />;
    if (contentType.startsWith('video/')) return <VideoFile />;
    if (contentType.startsWith('audio/')) return <AudioFile />;
    if (contentType.includes('zip') || contentType.includes('rar')) return <Archive />;
    return <InsertDriveFile />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, mb: 2 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  // Non mostrare nulla se non ci sono file
  if (!Array.isArray(files) || files.length === 0) {
    return null;
  }

  return (
    <Paper sx={{ p: 3, mb: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        Materiali del Corso
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ py: 1, fontSize: '0.75rem', fontWeight: 600 }}>Nome File</TableCell>
              <TableCell sx={{ py: 1, fontSize: '0.75rem', fontWeight: 600 }}>Dimensione</TableCell>
              <TableCell sx={{ py: 1, fontSize: '0.75rem', fontWeight: 600 }}>Caricato da</TableCell>
              <TableCell sx={{ py: 1, fontSize: '0.75rem', fontWeight: 600 }}>Data</TableCell>
              <TableCell align="center" sx={{ py: 1, fontSize: '0.75rem', fontWeight: 600 }}>Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {files.map((file) => (
              <TableRow key={file.key} hover sx={{ '&:last-child td': { border: 0 } }}>
                <TableCell sx={{ py: 0.5 }}>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    {React.cloneElement(getFileIcon(file.contentType), { sx: { fontSize: '1rem' } })}
                    <Typography variant="caption" noWrap sx={{ fontSize: '0.75rem' }}>
                      {file.fileName}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ py: 0.5 }}>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                    {formatFileSize(file.size)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 0.5 }}>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                    {file.uploadedBy || 'Sconosciuto'}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 0.5 }}>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                    {(file.uploadDate || file.uploadedAt || file.lastModified) ? 
                      dayjs(file.uploadDate || file.uploadedAt || file.lastModified).locale('it').format('DD/MM HH:mm') : 
                      'N/A'
                    }
                  </Typography>
                </TableCell>
                <TableCell align="center" sx={{ py: 0.5 }}>
                  <Tooltip title="Scarica">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleDownload(file)}
                      sx={{ p: 0.5 }}
                    >
                      <Download sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default CourseFilesMember;
